import { useQuery } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useNavigate, useParams } from "react-router-dom";
import { getStory, uploadFinalRecording } from "../api/stories";
import { AudioRecorder } from "../components/AudioRecorder";
import { InkDivider } from "../components/InkDivider";
import { RisographBackground } from "../components/RisographBackground";
import {
	attemptUpload,
	getRecording,
	saveRecording,
} from "../lib/recordingStorage";
import type { StoryDetail } from "../types";

type Mode = "read" | "retell";
type Phase = "select" | "recording";

export function ReRecordPage() {
	const { id } = useParams<{ id: string }>();
	const navigate = useNavigate();
	const { t } = useTranslation();

	const { data: story } = useQuery<StoryDetail>({
		queryKey: ["story", id],
		queryFn: () => getStory(id as string),
		enabled: !!id,
	});

	const [mode, setMode] = useState<Mode | null>(null);
	const [phase, setPhase] = useState<Phase>("select");
	const [audioBlob, setAudioBlob] = useState<Blob | null>(null);
	const [saving, setSaving] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [needsManualRetry, setNeedsManualRetry] = useState(false);

	// On mount: check for a recording that failed to upload in a previous session.
	useEffect(() => {
		if (!id) return;
		let cancelled = false;
		const key = `final:${id}`;

		async function recover() {
			const stored = await getRecording(key);
			if (!stored || cancelled) return;

			if (stored.failCount >= 3) {
				setAudioBlob(stored.blob);
				setNeedsManualRetry(true);
				return;
			}

			setSaving(true);
			const result = await attemptUpload(key, (blob) =>
				uploadFinalRecording(id, blob),
			);
			if (cancelled) return;
			setSaving(false);

			if (result === "success") {
				navigate(`/stories/${id}`);
			} else if (result === "exceeded") {
				setAudioBlob(stored.blob);
				setNeedsManualRetry(true);
			}
		}

		recover();
		return () => {
			cancelled = true;
		};
	}, [id, navigate]);

	function handleSkip() {
		navigate(`/stories/${id}`);
	}

	function handleStartRecording() {
		if (!mode) return;
		setPhase("recording");
	}

	async function handleSave() {
		if (!audioBlob || !id) return;
		setSaving(true);
		setError(null);
		setNeedsManualRetry(false);

		const key = `final:${id}`;
		await saveRecording(key, audioBlob);
		const result = await attemptUpload(key, (blob) =>
			uploadFinalRecording(id, blob),
		);

		if (result === "success") {
			navigate(`/stories/${id}`);
		} else {
			setSaving(false);
			setNeedsManualRetry(true);
		}
	}

	const storyText = story?.latest_version?.full_text ?? "";

	if (phase === "recording") {
		return (
			<div className="min-h-screen flex flex-col px-6 py-12 max-w-2xl mx-auto">
				<RisographBackground />

				<button
					type="button"
					onClick={() => {
						setPhase("select");
						setAudioBlob(null);
					}}
					className="font-ui text-xs uppercase tracking-widest text-ink/40 hover:text-ink/70 transition-colors self-start mb-12"
				>
					{t("stories.new.back")}
				</button>

				<div className="flex-1 flex flex-col gap-6">
					<AudioRecorder
						onRecordingComplete={(blob) => setAudioBlob(blob)}
						onReset={() => setAudioBlob(null)}
					/>
					<InkDivider className="mb-8" />

					{mode === "read" && storyText && (
						<div>
							<p className="font-ui text-[10px] uppercase tracking-widest text-ink/40 mb-4">
								{t("stories.detail.rerecord.teleprompterLabel")}
							</p>
							<div className="bg-white/30 backdrop-blur-sm border border-ink/10 rounded-sm p-6">
								<p className="font-body text-ink/80 text-lg leading-relaxed whitespace-pre-wrap">
									{storyText}
								</p>
							</div>
						</div>
					)}

					{needsManualRetry && (
						<div className="flex flex-col gap-2">
							<p className="font-ui text-[10px] uppercase tracking-wide text-coral/80">
								{t("stories.detail.rerecord.uploadFailed")}
							</p>
							<button
								type="button"
								onClick={handleSave}
								disabled={saving}
								className="self-start font-ui text-[10px] uppercase tracking-widest text-ink/50 underline hover:text-ink/80 transition-colors disabled:opacity-40"
							>
								{saving
									? t("stories.detail.rerecord.saving")
									: t("stories.detail.rerecord.retryUpload")}
							</button>
						</div>
					)}

					{error && !needsManualRetry && (
						<p className="font-ui text-sm uppercase tracking-wide text-coral">
							{error}
						</p>
					)}

					<div className="flex items-center gap-6">
						{audioBlob && !needsManualRetry && (
							<button
								type="button"
								onClick={handleSave}
								disabled={saving}
								className="ink-btn px-8 py-3 font-ui text-sm uppercase tracking-widest disabled:opacity-30 disabled:cursor-not-allowed"
							>
								{saving
									? t("stories.detail.rerecord.saving")
									: t("stories.detail.rerecord.saveRecording")}
							</button>
						)}
						<button
							type="button"
							onClick={handleSkip}
							className="font-ui text-[10px] uppercase tracking-widest text-ink/30 hover:text-ink/60 transition-colors"
						>
							{t("stories.detail.rerecord.skipNow")}
						</button>
					</div>
				</div>
			</div>
		);
	}

	return (
		<div className="min-h-screen flex flex-col px-6 py-12 max-w-2xl mx-auto">
			<RisographBackground />

			<button
				type="button"
				onClick={handleSkip}
				className="font-ui text-xs uppercase tracking-widest text-ink/40 hover:text-ink/70 transition-colors self-start mb-12"
			>
				{t("stories.detail.done.backToStories")}
			</button>

			<p className="font-ui text-[10px] uppercase tracking-widest text-ink/40 mb-3">
				{t("stories.detail.rerecord.eyebrow")}
			</p>

			<h1 className="font-display text-3xl font-bold text-ink leading-snug mb-4">
				{t("stories.detail.rerecord.heading")}
			</h1>

			<p className="font-body italic text-ink/60 text-base leading-relaxed mb-8">
				{t("stories.detail.rerecord.body")}
			</p>

			{storyText && (
				<div className="story-preview bg-white/20 border-l-2 border-ink/20 pl-4 py-3 mb-8">
					<p className="font-body italic text-ink/55 text-sm leading-relaxed line-clamp-4">
						{storyText}
					</p>
				</div>
			)}

			<InkDivider className="mb-6" />

			<div className="flex flex-col gap-3 mb-6">
				<ModeCard
					id="read"
					title={t("stories.detail.rerecord.modeReadTitle")}
					desc={t("stories.detail.rerecord.modeReadDesc")}
					selected={mode === "read"}
					onSelect={() => setMode("read")}
				/>
				<ModeCard
					id="retell"
					title={t("stories.detail.rerecord.modeRetellTitle")}
					desc={t("stories.detail.rerecord.modeRetellDesc")}
					selected={mode === "retell"}
					onSelect={() => setMode("retell")}
				/>
			</div>

			<button
				type="button"
				onClick={handleStartRecording}
				disabled={!mode}
				className="ink-btn w-full py-3 font-ui text-sm uppercase tracking-widest disabled:opacity-30 disabled:cursor-not-allowed mb-4"
			>
				{t("stories.detail.rerecord.startRecording")}
			</button>

			<button
				type="button"
				onClick={handleSkip}
				className="self-center font-ui text-xs uppercase tracking-widest text-ink/30 hover:text-ink/60 transition-colors"
			>
				{t("stories.detail.rerecord.skipNow")}
			</button>
		</div>
	);
}

function ModeCard({
	id,
	title,
	desc,
	selected,
	onSelect,
}: {
	id: string;
	title: string;
	desc: string;
	selected: boolean;
	onSelect: () => void;
}) {
	return (
		<button
			type="button"
			id={`mode-${id}`}
			onClick={onSelect}
			className={`text-left w-full border rounded-sm px-5 py-4 transition-colors ${
				selected
					? "border-ink/50 bg-white/30"
					: "border-ink/15 bg-white/10 hover:bg-white/20"
			}`}
		>
			<p className="font-ui text-sm font-medium text-ink mb-1">{title}</p>
			<p className="font-body text-sm text-ink/55 leading-snug">{desc}</p>
		</button>
	);
}
